import { useRef, useState } from 'react';
import { getTarget, useIsomorphicLayoutEffectWithTarget } from './use-effect-with-target';
import { useEvent } from './use-event';

// https://github.com/rsms/inter/issues/602
const ERROR_MARGIN = 3;
/**
 * Conditionally applies fade effect to text.
 */
export const useTextFade = ({
  enabled: _enabled = true,
  fadeWidth: _fadeWidth = '1.5em',
  maxLines
}) => {
  const [lineHeight, setLineHeight] = useState(undefined);
  const [textOverflown, setTextOverflown] = useState(false);
  const textRef = useRef(null);
  const singleLine = maxLines === 1;
  const updateOverflow = useEvent(() => {
    const text = getTarget(textRef);
    if (!text) return;
    setLineHeight(getComputedStyle(text).getPropertyValue('line-height'));
    if (singleLine) {
      // Horizontal overflow
      setTextOverflown(text.offsetWidth < text.scrollWidth);
    } else if (maxLines && maxLines > 1) {
      // Vertical overflow
      setTextOverflown(text.offsetHeight < text.scrollHeight - ERROR_MARGIN);
    }
  });
  useIsomorphicLayoutEffectWithTarget(() => {
    const text = getTarget(textRef);
    if (!text || !_enabled) return;
    updateOverflow();

    // For detecting font change
    const mutationObserver = new MutationObserver(() => {
      updateOverflow();
    });
    mutationObserver.observe(text, {
      attributes: true,
      childList: true,
      subtree: true
    });

    // For detecting resize
    let resizeObserver;
    if (typeof window !== 'undefined' && 'ResizeObserver' in window && typeof maxLines !== 'undefined') {
      resizeObserver = new ResizeObserver(entries => {
        for (const _ of entries) {
          updateOverflow();
        }
      });
      resizeObserver.observe(text);
    }
    return () => {
      var _resizeObserver;
      mutationObserver.disconnect();
      (_resizeObserver = resizeObserver) == null || _resizeObserver.disconnect();
    };
  }, [maxLines, _enabled], textRef);
  const maxHeight = lineHeight && !Number.isNaN(parseFloat(lineHeight)) && maxLines && maxLines > 1 ? parseFloat(lineHeight) * maxLines : undefined;
  const isFadeEnabled = _enabled && (!maxLines || textOverflown);
  const maskImage = isFadeEnabled ? `linear-gradient(
                     90deg,
                     rgba(0, 0, 0, 1) calc(100% - var(--t-fade-width, 1.5em)),
                     rgba(0, 0, 0, 0)
                    ),
                     linear-gradient(270deg, #000, #000)` : undefined;
  const maskPosition = isFadeEnabled ? 'center bottom, center top' : undefined;
  const maskRepeat = isFadeEnabled ? 'no-repeat' : undefined;
  const maskSize = isFadeEnabled ? '100% var(--t-fade-height, 1em), 100% calc(100% - var(--t-fade-height, 1em))' : undefined;
  return {
    textRef,
    style: {
      '--t-fade-width': isFadeEnabled ? _fadeWidth : undefined,
      '--t-fade-height': isFadeEnabled ? lineHeight : undefined,
      overflow: maxLines ? 'hidden' : undefined,
      overflowWrap: _enabled && maxLines && maxLines > 1 ? 'break-word' : undefined,
      whiteSpace: singleLine ? 'nowrap' : undefined,
      maxHeight,
      maskImage,
      maskPosition,
      maskRepeat,
      maskSize,
      WebkitMaskImage: maskImage,
      WebkitMaskPosition: maskPosition,
      WebkitMaskRepeat: maskRepeat,
      WebkitMaskSize: maskSize
    }
  };
};